import React, { useEffect, useState, useMemo } from "react";
import { Box, Modal, Paper, Tooltip, Typography, Button } from "@mui/material";
import { RiTaskLine, RiCloseLine } from "@remixicon/react";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import useApi from "../../hooks/useGaneshApi";
import {
  GridColDef,
  GridInitialState,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import CancelJobDialog from "./Dialogs/CancelJobDialog";
import { getDateValue, renderHumanReadableDateCell } from "utils/dataGrid";

interface JobDetail {
  job_id: number;
  job_type: string;
  created_at: string;
  updated_at: string;
  admin_id: number;
  admin_name: string;
  dataset_type: string;
  place_set_id: number;
  place_set_name: string;
  dequeued_at?: string;
  data_loaded_at?: string;
  completed_at?: string;
  failed_at?: string;
  cancelled_at?: string;
  started_at?: string;
  error_message?: string;
}

interface Props {
  adminId: number;
  datasetType: string;
  intervalDays?: number;
  label?: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  endpoint?: "default" | "active" | "failed";
  exportType?: string;
}

const modalStyle = {
  position: "absolute" as const,
  top: "0%",
  left: "50%",
  transform: "translateX(-50%)",
  width: "75%",
  height: "100%",
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column",
  p: 3,
  borderRadius: 2,
  boxShadow: 24,
  overflow: "hidden",
};

const computeJobStatus = (job: JobDetail): string => {
  if (job.completed_at) return "Complete";
  if (job.failed_at && !job.completed_at) return "Failed";
  if (
    job.started_at &&
    !job.completed_at &&
    !job.failed_at &&
    !job.cancelled_at
  )
    return "In Progress";
  if (!job.dequeued_at && !job.started_at) return "Queued";
  if (job.dequeued_at && !job.started_at) return "Dequeued";
  return "Unknown";
};

const AnalysisJobDetailsModal: React.FC<Props> = ({
  adminId,
  datasetType,
  intervalDays,
  label = "Job Details",
  isOpen,
  onOpen,
  onClose,
  endpoint,
  exportType,
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [jobData, setJobData] = useState<JobDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const { apiRequest } = useApi();
  const endpointPath =
    endpoint === "active"
      ? `/v1/export-jobs/jobs/active?admin_id=${adminId}&dataset_type=${datasetType}${
          exportType ? `&export_type=${exportType}` : ""
        }`
      : endpoint === "failed"
      ? `/v1/export-jobs/jobs?admin_id=${adminId}&dataset_type=${datasetType}&interval_days=${intervalDays}&status=failed${
          exportType ? `&export_type=${exportType}` : ""
        }`
      : `/v1/export-jobs/jobs?admin_id=${adminId}&dataset_type=${datasetType}&interval_days=${intervalDays}${
          exportType ? `&export_type=${exportType}` : ""
        }`;

  const fetchJobData = async () => {
    setIsLoading(true);
    try {
      const res = await apiRequest<{ data: JobDetail[] }>(endpointPath);
      if (res.data) {
        // Handle the new success_response format
        const data = res.data.data || res.data;
        setJobData(data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchJobData();
  }, [adminId, datasetType, intervalDays, isOpen, exportType]);

  const transformedData = useMemo(() => {
    return jobData.map((job) => ({
      ...job,
      id: job.job_id,
      status: computeJobStatus(job),
    }));
  }, [jobData]);

  const defaultGridState: GridInitialState = {
    filter: {
      filterModel: {
        items: [],
      },
    },
    sorting: { sortModel: [] },
    pagination: { paginationModel: { pageSize: 25, page: 0 } },
    pinnedColumns: { left: [] },
    columns: { columnVisibilityModel: {} },
    density: "compact",
  };

  const columns_def: GridColDef[] = useMemo(() => {
    const baseCols: GridColDef[] = [
      { field: "job_id", headerName: "Job ID", width: 100 },
      { field: "place_set_id", headerName: "Place Set ID", width: 100 },
      { field: "place_set_name", headerName: "Place Set Name", width: 200 },
      { field: "status", headerName: "Status", width: 100 },
      {
        field: "created_at",
        headerName: "Created",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
      {
        field: "dequeued_at",
        headerName: "Dequeued",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
      {
        field: "data_loaded_at",
        headerName: "Data Loaded",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
      {
        field: "completed_at",
        headerName: "Completed",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
      {
        field: "failed_at",
        headerName: "Failed",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
      {
        field: "cancelled_at",
        headerName: "Cancelled",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
      {
        field: "updated_at",
        headerName: "Updated",
        width: 150,
        type: "date",
        valueGetter: getDateValue,
        renderCell: renderHumanReadableDateCell,
      },
    ];
    if (endpoint === "failed") {
      baseCols.splice(3, 0, {
        field: "error_message",
        headerName: "Error Message",
        width: 300,
        renderCell: (params) => (
          <Tooltip title={params.value || "No error message"}>
            <Typography noWrap>{params.value}</Typography>
          </Tooltip>
        ),
      });
    }
    if (endpoint === "active") {
      baseCols.splice(3, 0, {
        field: "export_type",
        headerName: "Export Type",
        width: 150,
      });
    }
    return baseCols;
  }, [endpoint]);

  // Get selected job IDs for cancellation
  const selectedJobIds = useMemo(() => {
    return rowSelectionModel.map((id) => Number(id));
  }, [rowSelectionModel]);

  // Handle row selection changes
  const handleRowSelectionModelChange = (
    newRowSelectionModel: GridRowSelectionModel
  ) => {
    setRowSelectionModel(newRowSelectionModel);
  };

  return (
    <Box display="flex" alignItems="center">
      <Tooltip title={`View ${label}`}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RiTaskLine size={20} />}
          onClick={onOpen}
        >
          View Jobs
        </Button>
      </Tooltip>

      <Modal open={isOpen} onClose={onClose}>
        <Paper sx={modalStyle}>
          <Box mb={2}>
            <Typography variant="h6">
              {label} (Admin ID: {adminId}, Admin Name:{" "}
              {jobData[0]?.admin_name || "N/A"}, Dataset: {datasetType}
              {exportType && `, Export Type: ${exportType}`}
              {endpoint !== "active" && `, Interval: ${intervalDays} days`})
            </Typography>
          </Box>
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <Button onClick={onClose} size="small" variant="text">
              <RiCloseLine size={20} />
            </Button>
          </Box>
          {endpoint === "active" && (
            <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                color="error"
                onClick={() => setShowCancelDialog(true)}
                disabled={selectedJobIds.length === 0}
              >
                Cancel Selected Jobs ({selectedJobIds.length})
              </Button>
              {selectedJobIds.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {selectedJobIds.length} job
                  {selectedJobIds.length !== 1 ? "s" : ""} selected
                </Typography>
              )}
            </Box>
          )}
          {showCancelDialog && selectedJobIds.length > 0 && (
            <CancelJobDialog
              jobIds={selectedJobIds}
              open={true}
              onClose={() => {
                setShowCancelDialog(false);
                setRowSelectionModel([]);
              }}
              onSuccess={() => {
                setShowCancelDialog(false);
                setRowSelectionModel([]);
                fetchJobData();
              }}
            />
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <GaneshDataGrid
              data={transformedData}
              loading={isLoading}
              defaultGridState={defaultGridState}
              columns={columns_def}
              gridId={`admin-jobs-${adminId}`}
              checkboxSelection={endpoint === "active"}
              disableRowSelectionOnClick={endpoint === "active"}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              keepNonExistentRowsSelected
              sx={{
                height: "85vh",
                width: "100%",
                "& .MuiDataGrid-footerContainer": {
                  borderTop: 1,
                  borderColor: "divider",
                },
              }}
            />
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default AnalysisJobDetailsModal;
